import { useEffect, useRef, useState } from 'react';
import { CalendarPlus, RotateCcw } from 'lucide-react';
import ActivationModal from './ActivationModal.jsx';
import { api, ApiError } from '../api/client.js';

// The Tournament card (licensing brief §10.1) — replaces the START GG card
// for billing-enforced users. Three faces: test mode (watermarked sample
// data + balance + activate), live (the on-air run: events, stream picker,
// expiry line, add-event, per-event undo), grace (same + countdown). The
// on-air selector appears only when a second unexpired run exists. The
// mismatch banner (§7.1) names queue sets from non-activated events — the
// strictness explained, with the fix one click away.

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);

// License history (brief §4: expired runs are the client's tournament
// history). A run enters this fold only once runStatus says 'expired' —
// i.e. every activated event finished and the 24 h grace ended (or the hard
// ceiling passed). Until then its credits are visible on the live face
// above, so each used run is always in exactly one place. Voided
// (refunded) activations never appear — the server excludes them at the
// query. Purchases are fulfilled orders only; adjustments are shown as
// bare deltas (the reason is admin-internal).
//
// Rendered in the admin-table language (adm-grid rows, Orbitron heads,
// gradient rule, staggered adm-rvl reveal — replays each time the fold
// opens, because a closed <details> is display:none and animations only
// start once the rows become visible).
function LicenseHistory({ packageSlug, runs, purchases, adjustments, balance }) {
  const [openStandings, setOpenStandings] = useState(null);
  // Open by default, user-closable. State-controlled on purpose: a bare
  // `open` attribute would be re-asserted by React on every billing
  // refresh, snapping the fold back open after the user closed it.
  const [foldOpen, setFoldOpen] = useState(true);
  const expired = runs.filter((r) => r.status === 'expired');
  const rows = expired.flatMap((r) =>
    r.activations.filter((a) => !a.voided).map((a) => ({ run: r, a })));
  const totalUsed = runs.reduce((n, r) => n + r.activations.filter((a) => !a.voided).length, 0);
  const totalPurchased = purchases.reduce((n, o) => n + o.quantity, 0);
  const adjNet = adjustments.reduce((n, a) => n + a.delta, 0);
  const credits = [
    ...purchases.map((o) => ({ id: o.id, at: o.at, kind: 'Purchase', delta: o.quantity, amount: `${Number(o.amount).toFixed(3)} ${o.currency}` })),
    ...adjustments.map((a) => ({ id: a.id, at: a.at, kind: 'Adjustment', delta: a.delta, amount: null })),
  ].sort((x, y) => new Date(y.at) - new Date(x.at));
  if (!rows.length && !credits.length) return null;

  const archiveTitle = 'First download renders the archive (~10 s); after that it’s instant.';

  return (
    <details
      className="card-fold mt-3"
      open={foldOpen}
      onToggle={(e) => setFoldOpen(e.currentTarget.open)}
    >
      <summary className="small">
        License history — {totalUsed} run{totalUsed === 1 ? '' : 's'} used
        {expired.length > 0 && ` · ${expired.length} completed tournament${expired.length === 1 ? '' : 's'}`}
      </summary>

      {rows.length > 0 && (
        /* Fixed-width archive column: the head and every row are separate
           grid containers, so an `auto` track resolves per-container (narrow
           "ARCHIVE" label vs wide chips) and skews the RUNS column. */
        <div className="adm-scroll lh-grid mt-2" style={{ '--adm-cols': '1.7fr 1.4fr 0.4fr 240px', '--adm-minw': '620px' }}>
          <div className="adm-grid__head">
            <span>Tournament</span>
            <span>Winner</span>
            <span>Runs</span>
            <span className="is-right">Archive</span>
          </div>
          <div className="adm-grid__rule" aria-hidden="true" />
          {rows.map(({ run, a }, i) => {
            const w = a.top8?.winner;
            const eventDiffers = a.eventName && a.eventName !== (run.name ?? run.slug);
            return (
              <div key={a.id}>
                <div className="adm-grid__row adm-rvl" style={{ animationDelay: `${60 + i * 45}ms` }}>
                  <div className="lh-t">
                    <span className="lh-t__name">{run.name ?? run.slug}</span>
                    <span className="lh-t__sub">
                      {fmtDate(run.startAt) ? `${fmtDate(run.startAt)} – ${fmtDate(run.endAt) ?? '…'}` : ''}
                      {eventDiffers ? ` · ${a.eventName}` : ''}
                    </span>
                  </div>
                  <div className="adm-cell adm-cell--dim">
                    {w ? (
                      <>
                        <span className="lh-w">{[w.prefix, w.tag].filter(Boolean).join(' ')}</span>
                        {w.character && <span className="lh-w__char"> · {w.character}</span>}
                      </>
                    ) : (
                      <span className="adm-cell--faint">—</span>
                    )}
                    {a.top8 && (
                      <button
                        type="button"
                        className="lh-standings-toggle"
                        onClick={() => setOpenStandings(openStandings === a.id ? null : a.id)}
                      >
                        {openStandings === a.id ? 'hide' : 'standings'}
                      </button>
                    )}
                  </div>
                  <div className="adm-cell adm-cell--dim">1</div>
                  <div className="adm-grid__actions">
                    {a.top8 ? (
                      <>
                        <a className="btn-ghost lh-dl" href={`/api/packages/${packageSlug}/history/${a.id}/top8-image`} title={archiveTitle}>
                          Top 8 PNG
                        </a>
                        <a className="btn-ghost lh-dl" href={`/api/packages/${packageSlug}/history/${a.id}/top8-image?format=portrait`} title={archiveTitle}>
                          Instagram (3:4)
                        </a>
                      </>
                    ) : (
                      <span className="adm-cell adm-cell--faint">no snapshot</span>
                    )}
                  </div>
                </div>
                {openStandings === a.id && a.top8 && (
                  <div className="lh-standings adm-rv">
                    <ol>
                      {a.top8.entries.map((e, j) => (
                        <li key={j} value={e.place ?? j + 1}>
                          {[e.prefix, e.tag].filter(Boolean).join(' ')}
                          {e.character ? ` — ${e.character}` : ''}
                          {e.unsealed ? ' (unsealed)' : ''}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {credits.length > 0 && (
        <div className="adm-scroll lh-grid mt-3" style={{ '--adm-cols': '0.9fr 1fr 0.5fr 0.9fr', '--adm-minw': '420px' }}>
          <div className="adm-grid__head">
            <span>Date</span>
            <span>Credits</span>
            <span>Runs</span>
            <span className="is-right">Amount</span>
          </div>
          <div className="adm-grid__rule" aria-hidden="true" />
          {credits.map((c, i) => (
            <div className="adm-grid__row adm-rvl" key={c.id} style={{ animationDelay: `${60 + (rows.length + i) * 45}ms` }}>
              <div className="adm-cell adm-cell--dim">{fmtDate(c.at)}</div>
              <div className="adm-cell adm-cell--dim">{c.kind}</div>
              <div className={`adm-cell adm-cell--dim ${c.delta >= 0 ? 'lh-delta--pos' : 'lh-delta--neg'}`}>
                {c.delta > 0 ? '+' : ''}{c.delta}
              </div>
              <div className="adm-cell adm-cell--dim is-right lh-amount">{c.amount ?? '—'}</div>
            </div>
          ))}
        </div>
      )}

      <div className="lh-total d-flex justify-content-between">
        <span>
          Purchased {totalPurchased}
          {adjNet !== 0 && ` · adjusted ${adjNet > 0 ? '+' : ''}${adjNet}`}
          {' '}· used {totalUsed}
        </span>
        <span>Balance {balance}</span>
      </div>
    </details>
  );
}

const RUN_PRICE_KWD = 55; // display only — the server prices the order

// Inline run-credit checkout: quantity picker → hosted UPayments page (the
// whole tab redirects; /billing/return brings the client back). A 503 from
// checkout means online payments aren't configured — the flash relays the
// server's "runs are added by your admin" message.
function BuyRuns({ packageSlug, flash }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    try {
      const res = await api.packageCheckout(packageSlug, qty);
      if (res?.url) { window.location.assign(res.url); return; }
      flash('danger', 'Checkout failed — try again.');
    } catch (e) {
      flash(e instanceof ApiError && e.status === 503 ? 'info' : 'danger', e.message);
    }
    setBusy(false);
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn-ghost"
        onClick={() => setOpen(true)}
      >
        Buy runs
      </button>
    );
  }
  return (
    <span className="d-inline-flex align-items-center gap-2 flex-wrap">
      <select
        className="form-select form-select-sm"
        style={{ width: 'auto' }}
        value={qty}
        onChange={(e) => setQty(Number(e.target.value))}
        aria-label="How many runs"
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <option key={n} value={n}>{n} × {RUN_PRICE_KWD} = {n * RUN_PRICE_KWD} KWD</option>
        ))}
      </select>
      <button type="button" className="btn-pill btn-pill--primary" disabled={busy} onClick={go}>
        {busy ? 'Redirecting…' : 'Checkout'}
      </button>
      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOpen(false)}>
        cancel
      </button>
    </span>
  );
}

function untilText(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 'now';
  const h = Math.floor(ms / 3600_000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))} min`;
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} days`;
}

// historyOnly: the unenforced-console variant — no faces, no activation
// modal, no on-air feed; just the license history (with archived downloads)
// in its own card, and only when the billing read has something to show.
// Unenforced accounts only carry runs when an admin backfilled them, so for
// everyone else this renders nothing and the console stays pixel-identical.
// showHistory: admin-set per-user flag (users.tournament_history_enabled) —
// hides the license-history fold without touching the live faces above it.
// The unenforced historyOnly card is gated at its mount site instead, so a
// hidden history skips the billing fetch entirely.
export default function TournamentCard({ packageSlug, onFlash, onOnAirSlug, historyOnly = false, showHistory = true }) {
  const [data, setData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [queueInfo, setQueueInfo] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [streamDraft, setStreamDraft] = useState(null);

  // onFlash rides a ref: the parent console re-renders constantly
  // (autosave), and a fresh callback identity must not refetch billing.
  const flashRef = useRef(onFlash);
  flashRef.current = onFlash;
  const flash = (kind, msg) => flashRef.current?.(kind, msg);

  function load() {
    api.packageBilling(packageSlug)
      .then(setData)
      .catch((e) => flash('danger', e instanceof ApiError ? e.message : 'Billing load failed'));
  }

  useEffect(() => { load(); }, [packageSlug]);

  const runs = data?.runs ?? [];
  const onAir = runs.find((r) => r.onAir) ?? null;
  const unexpired = runs.filter((r) => r.status !== 'expired');

  // Feed the on-air tournament slug up (the Top 8 snapshot card's event
  // dropdown resolves from it under enforcement).
  useEffect(() => { onOnAirSlug?.(onAir?.slug ?? null); }, [onAir?.slug, onOnAirSlug]);

  // Mismatch banner poll — gentle (45s), only while a run is on air.
  useEffect(() => {
    if (!onAir) { setQueueInfo(null); return undefined; }
    let alive = true;
    const poll = () => api.packageQueueCheck(packageSlug)
      .then((q) => { if (alive) setQueueInfo(q); })
      .catch(() => { /* banner is best-effort */ });
    poll();
    const t = setInterval(poll, 45_000);
    return () => { alive = false; clearInterval(t); };
  }, [packageSlug, onAir?.id]);

  async function undo(activationId) {
    setBusyId(activationId);
    try {
      const res = await api.packageUndoActivation(packageSlug, activationId);
      if (res?.ok) {
        flash('success', `Activation undone — run refunded (balance ${res.balance}).`);
        load();
      } else if (res?.reason === 'started') {
        flash('warning', 'That bracket has started — undo is now an admin-mediated refund.');
      } else {
        flash('danger', `Undo failed (${res?.reason ?? 'unknown'}).`);
      }
    } catch (e) {
      flash('danger', e instanceof ApiError ? e.message : 'Undo failed');
    } finally {
      setBusyId(null);
    }
  }

  async function saveStream() {
    if (streamDraft == null || !onAir) return;
    const next = streamDraft.trim();
    setStreamDraft(null);
    if (next === (onAir.streamName ?? '')) return;
    try {
      await api.packageSetRunStream(packageSlug, onAir.id, next);
      flash('success', next ? `Stream set to ${next}.` : 'Stream cleared.');
      load();
    } catch (e) {
      flash('danger', e instanceof ApiError ? e.message : 'Stream update failed');
    }
  }

  async function switchOnAir(userTournamentId) {
    try {
      await api.packageSetOnAir(packageSlug, userTournamentId);
      load();
    } catch (e) {
      flash('danger', e instanceof ApiError ? e.message : 'Switch failed');
    }
  }

  const mismatches = (queueInfo?.groups ?? []).filter((g) => !g.activated && g.count > 0);

  if (historyOnly) {
    if (!data) return null;
    const hasHistory = runs.some((r) => r.status === 'expired' && r.activations.some((a) => !a.voided))
      || (data.purchases?.length ?? 0) > 0 || (data.adjustments?.length ?? 0) > 0;
    if (!hasHistory) return null;
    return (
      <div className="card card-fui mb-4">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 className="mb-0">Tournament history</h6>
        </div>
        <div className="card-body">
          <LicenseHistory
            packageSlug={packageSlug}
            runs={runs}
            purchases={data.purchases ?? []}
            adjustments={data.adjustments ?? []}
            balance={data.balance}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="card card-fui mb-4">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h6 className="mb-0">Tournament</h6>
        <span className="adm-card-count">
          {data ? `${data.balance} run${data.balance === 1 ? '' : 's'} available` : '…'}
        </span>
      </div>
      <div className="card-body">
        {!data ? (
          <p className="text-muted small mb-0">Loading…</p>
        ) : !onAir ? (
          /* ── test mode face ── */
          <>
            <p className="mb-2">
              <strong>Test mode</strong> — your overlays are watermarked and showing
              sample data. Everything else (titles, sponsors, countdowns) is live from
              this console, so you can rehearse your full setup.
            </p>
            <p className="text-muted small mb-3">
              Activating a tournament spends 1 run per event, binds your overlays to
              it, and clears the watermark on the next poll — OBS untouched.
              {data.balance === 0 && ' Runs are added by your admin.'}
            </p>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn-pill btn-pill--primary"
                onClick={() => setModalOpen(true)}
              >
                <CalendarPlus size={14} aria-hidden="true" />
                Activate a tournament
              </button>
              <BuyRuns packageSlug={packageSlug} flash={flash} />
            </div>
          </>
        ) : (
          /* ── live / grace face ── */
          <>
            {unexpired.length > 1 && (
              <div className="mb-3">
                <label className="form-label small mb-1" htmlFor="onair-select">On air</label>
                <select
                  id="onair-select"
                  className="form-select form-select-sm"
                  value={onAir.id}
                  onChange={(e) => switchOnAir(e.target.value)}
                >
                  {unexpired.map((r) => (
                    <option value={r.id} key={r.id}>{r.name ?? r.slug}</option>
                  ))}
                </select>
              </div>
            )}

            <p className="mb-1">
              <strong>{onAir.name ?? onAir.slug}</strong>
              {onAir.status === 'grace' && (
                <span className="status-tag status-tag--work ms-2">GRACE</span>
              )}
            </p>
            <p className="text-muted small mb-3">
              {fmtDate(onAir.startAt) && `${fmtDate(onAir.startAt)} – ${fmtDate(onAir.endAt) ?? '…'} · `}
              {onAir.status === 'grace'
                ? `watermark returns in ${untilText(onAir.graceEndsAt)}`
                : `watermark returns 24 h after your last event finishes (hard limit ${untilText(onAir.ceilingAt)})`}
            </p>

            {mismatches.map((g) => (
              <div className="alert alert-warning py-2 small mb-3" key={g.eventId} role="alert">
                Your stream queue has {g.count} set{g.count === 1 ? '' : 's'} from{' '}
                <strong>{g.eventName ?? `event ${g.eventId}`}</strong>, which isn&apos;t
                activated — those sets won&apos;t appear on your overlays.{' '}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => setModalOpen(true)}
                >
                  Activate it (1 run)
                </button>
              </div>
            ))}
            {queueInfo?.streamConfigured === false && (
              <div className="alert alert-warning py-2 small mb-3" role="alert">
                No stream channel set — your overlays don&apos;t know which channel to
                watch. Set it below.
              </div>
            )}

            <ul className="list-unstyled small mb-3">
              {onAir.activations.filter((a) => !a.voided).map((a) => (
                <li className="d-flex align-items-center gap-2 mb-1" key={a.id}>
                  <span className={`status-tag ${a.completedSeenAt ? 'status-tag--wait' : 'status-tag--ok'}`}>
                    {a.completedSeenAt ? 'FINISHED' : 'LIVE'}
                  </span>
                  <span>{a.eventName ?? a.eventId}</span>
                  {!a.completedSeenAt && (
                    <button
                      type="button"
                      className="btn-icon"
                      disabled={busyId === a.id}
                      onClick={() => undo(a.id)}
                      title="Undo this activation (free until its bracket starts)"
                      aria-label={`Undo ${a.eventName ?? a.eventId}`}
                    >
                      <RotateCcw size={14} aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>

            <div className="row g-2 align-items-end">
              <div className="col-md-5">
                <label className="form-label small mb-1" htmlFor="run-stream">Stream channel</label>
                <input
                  id="run-stream"
                  className="form-control form-control-sm"
                  value={streamDraft ?? onAir.streamName ?? ''}
                  onChange={(e) => setStreamDraft(e.target.value)}
                  onBlur={saveStream}
                  placeholder="twitch channel name"
                />
              </div>
              <div className="col-auto">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setModalOpen(true)}
                >
                  <CalendarPlus size={14} aria-hidden="true" />
                  Add event / tournament
                </button>
              </div>
              <div className="col-auto">
                <BuyRuns packageSlug={packageSlug} flash={flash} />
              </div>
            </div>
          </>
        )}

        {data && showHistory && (
          <LicenseHistory
            packageSlug={packageSlug}
            runs={runs}
            purchases={data.purchases ?? []}
            adjustments={data.adjustments ?? []}
            balance={data.balance}
          />
        )}
      </div>

      <ActivationModal
        packageSlug={packageSlug}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onActivated={(res) => {
          flash('success', `Activated — ${res.activationIds?.length ?? ''} event(s), balance ${res.balance}. Watermark clears on the next poll.`);
          load();
        }}
      />
    </div>
  );
}
